import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface QRData {
    url: string;
    timestamp: number;
    session: string | null;
}

interface PinData {
    pin: string;
    timestamp: number;
    timeoutId: NodeJS.Timeout | null;
}

interface SponsorData {
    currentSerial: string;
    currentSubjectDN: string;
    newSerial: string;
}

interface PinFormData {
    show: boolean;
    title?: string;
    error?: string;
    callback?: (pin: string) => void;
    cancelCallback?: () => void;
    sponsorData?: SponsorData;
}

interface IssuanceError {
    type: string;
    message: string;
}

interface WorkflowContextValue {
    qrData: QRData | null;
    clearQR: () => void;
    qrTimeoutSeconds: number | null;
    qrConsumed: boolean;
    issuanceCompleted: boolean;
    issuanceError: IssuanceError | null;
    pin: string | null;
    setPin: (pin: string, timeoutMs?: number) => void;
    getPin: () => string | null;
    hasPin: () => boolean;
    clearPin: () => void;
    // PIN form control
    pinFormData: PinFormData | null;
    clearPinForm: () => void;
}

const WorkflowContext = createContext<WorkflowContextValue | null>(null);

export const WorkflowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [qrData, setQRData] = useState<QRData | null>(null);
    const [qrTimeoutSeconds, setQRTimeoutSeconds] = useState<number | null>(null);
    const [qrConsumed, setQRConsumed] = useState(false);
    const [issuanceCompleted, setIssuanceCompleted] = useState(false);
    const [issuanceError, setIssuanceError] = useState<IssuanceError | null>(null);
    const [pinData, setPinData] = useState<PinData | null>(null);
    const [pinFormData, setPinFormData] = useState<PinFormData | null>(null);

    const setPin = useCallback((pin: string, timeoutMs?: number) => {
        console.log('Setting PIN', timeoutMs ? `with ${timeoutMs}ms timeout` : '');

        // Clear any existing timeout
        if (pinData?.timeoutId) {
            clearTimeout(pinData.timeoutId);
        }

        // Set up new timeout if specified
        let timeoutId: NodeJS.Timeout | null = null;
        if (timeoutMs && timeoutMs > 0) {
            timeoutId = setTimeout(() => {
                console.log('PIN timeout expired, clearing PIN');
                setPinData(null);
            }, timeoutMs);
        }

        setPinData({
            pin,
            timestamp: Date.now(),
            timeoutId
        });
    }, [pinData]);

    const getPin = useCallback((): string | null => {
        return pinData?.pin || null;
    }, [pinData]);

    const hasPin = useCallback((): boolean => {
        return pinData !== null && pinData.pin !== null && pinData.pin !== '';
    }, [pinData]);

    const clearPin = useCallback(() => {
        console.log('Clearing PIN');
        if (pinData?.timeoutId) {
            clearTimeout(pinData.timeoutId);
        }
        setPinData(null);
    }, [pinData]);

    const clearQR = useCallback(() => {
        setQRData(null);
        setQRTimeoutSeconds(null);
        setQRConsumed(false);
        setIssuanceCompleted(false);
        setIssuanceError(null);
    }, []);
    const clearPinForm = useCallback(() => setPinFormData(null), []);

    useEffect(() => {
        window.proxy = {
            next: (session: string, stage: string, step: string, token: string) => {
                // Mobile should call /apdu/next to continue workflow after Portal displays QR
                // Portal already executed the first operation (QR display) by calling /apdu/start
                const qrUrl = `${window.location.origin}/user/api/apdu/next?session=${session}&stage=${stage}&step=${step}&token=${token}`;
                console.log('Generated QR URL:', qrUrl);
                setQRData({ url: qrUrl, timestamp: Date.now(), session });
                setQRConsumed(false);
            },

            // QR lifecycle methods
            setQRTimeout: (seconds: number) => {
                console.log('Setting QR timeout:', seconds, 'seconds');
                setQRTimeoutSeconds(seconds);
            },

            qrConsumed: () => {
                console.log('QR code consumed');
                setQRConsumed(true);
            },

            issuanceCompleted: () => {
                console.log('Issuance completed');
                setIssuanceCompleted(true);
            },

            issuanceError: (message: string, errorType?: string) => {
                console.log('Issuance error:', message, errorType || 'error');
                setIssuanceError({ type: errorType || 'error', message });
            },

            // PIN form display methods
            showPinForm: (title?: string, error?: string, sponsorData?: SponsorData, cancelCallback?: () => void) => {
                console.log('Showing PIN form', { title, error, sponsorData });
                setPinFormData({
                    show: true,
                    title,
                    error,
                    sponsorData,
                    cancelCallback,
                    callback: (pin: string) => {
                        // Store the PIN when user submits
                        setPin(pin);
                        // Trigger workflow continuation
                        if ((window.proxy as any).onPinSubmit) {
                            (window.proxy as any).onPinSubmit(pin);
                        }
                    }
                });
            },

            hidePinForm: () => {
                console.log('Hiding PIN form');
                setPinFormData(prev => prev ? { ...prev, show: false } : null);
            },

            clearPinForm: () => {
                setPinFormData(null);
            },

            setPinError: (error: string) => {
                setPinFormData(prev => prev ? { ...prev, error } : null);
            },

            // PIN management
            setPin: (pin: string, timeoutMs?: number) => setPin(pin, timeoutMs),
            getPin: () => getPin(),
            hasPin: () => hasPin(),
            clearPin: () => clearPin(),

            // Callback placeholder for PIN submission
            onPinSubmit: null as ((pin: string) => void) | null,
            // Callback placeholder for PIN cancellation
            onPinCancel: null as (() => void) | null
        };

        return () => {
            // Cleanup: clear PIN timeout on unmount
            if (pinData?.timeoutId) {
                clearTimeout(pinData.timeoutId);
            }
            delete window.proxy;
        };
    }, [setPin, getPin, hasPin, clearPin, pinData, clearQR]);

    return (
        <WorkflowContext.Provider value={{
            qrData,
            clearQR,
            qrTimeoutSeconds,
            qrConsumed,
            issuanceCompleted,
            issuanceError,
            pin: pinData?.pin || null,
            setPin,
            getPin,
            hasPin,
            clearPin,
            pinFormData,
            clearPinForm
        }}>
            {children}
        </WorkflowContext.Provider>
    );
};

export const useWorkflow = () => {
    const context = useContext(WorkflowContext);
    if (!context) throw new Error('useWorkflow must be used within WorkflowProvider');
    return context;
};

declare global {
    interface Window {
        proxy?: {
            next: (session: string, stage: string, step: string, token: string) => void;
            // QR lifecycle
            setQRTimeout: (seconds: number) => void;
            qrConsumed: () => void;
            issuanceCompleted: () => void;
            issuanceError: (message: string, errorType?: string) => void;
            // PIN form display
            showPinForm: (title?: string, error?: string, sponsorData?: SponsorData, cancelCallback?: () => void) => void;
            hidePinForm: () => void;
            clearPinForm: () => void;
            setPinError: (error: string) => void;
            // PIN management
            setPin: (pin: string, timeoutMs?: number) => void;
            getPin: () => string | null;
            hasPin: () => boolean;
            clearPin: () => void;
            // Callback for PIN submission
            onPinSubmit: ((pin: string) => void) | null;
            // Callback for PIN cancellation
            onPinCancel: (() => void) | null;
        };
    }
}
